import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Breadcrumbs, BreadcrumbItem, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button } from "@heroui/react";
import { ChevronDown } from "lucide-react";
import { fetchUserMenu } from '../dashboard/dashboard.actions.js';
import { launchTraining } from '../../api/trainingSsoLaunch.js';

/**
 * Modern breadcrumb navigation component using HeroUI
 *
 * @param {Object} props
 * @param {string} props.currentPage - Current page name (e.g., "Locations")
 * @param {Array} props.customItems - Optional custom breadcrumb items to insert before current page
 */
const HeroUIBreadcrumb = ({ currentPage, customItems = [] }) => {
  const dispatch = useDispatch();
  // Support both 'dashboard' and 'DashboardReducer' keys for compatibility
  const userMenu = useSelector(state => state.dashboard?.userMenu || state.DashboardReducer?.userMenu || []);
  const isLoading = useSelector(state => state.dashboard?.isLoading || state.DashboardReducer?.isLoading || false);
  // Get organization settings to check if Training module is enabled
  // Read from DashboardReducer which is populated by fetchUserMenu
  const organizationSettings = useSelector(state =>
    state.dashboard?.organizationSettings || state.DashboardReducer?.organizationSettings
  );
  // Calendar Month V2 detection: only that page mounts the `calendarMonthBootstrap`
  // slice (status is 'idle' from the first render, truthy). On that page the
  // bootstrap thunk fetches the SAME /org/{u}/dashboard payload and shares
  // `userMenu` into `state.dashboard`, so the breadcrumb must NOT fire its own
  // duplicate request. On the other 18 pages this slice is absent (undefined)
  // → the breadcrumb fetches exactly as before. (Selector-by-key, no import, so
  // the calendar slice isn't pulled into every page's bundle.)
  const calendarBootstrapStatus = useSelector(
    state => state.calendarMonthBootstrap?.status
  );

  const {
    _org = '',
    organizationsDataForCalendars = []
  } = window.serverData || {};

  const orgUnique = _org.split('/').at(-1);
  const orgName = organizationsDataForCalendars.find(org => org.unique === orgUnique)?.name || 'Organization';

  React.useEffect(() => {
    // Only fetch in organization context (orgUnique exists)...
    if (!orgUnique) return;
    // ...skip on the Calendar Month V2 page — its bootstrap owns the single
    // /org/{u}/dashboard fetch and shares `userMenu` into `state.dashboard`...
    if (calendarBootstrapStatus) return;
    // ...and don't refetch if a menu is already loaded.
    if (userMenu.length > 0) return;
    dispatch(fetchUserMenu(orgUnique));
  }, [dispatch, orgUnique, calendarBootstrapStatus, userMenu.length]);

  // Temporary URL mapping until frontend routing is fully implemented
  // Maps userMenu 'unique' field to actual URL paths
  const MENU_URL_MAP = {
    'dashboard': 'organizations/dashboard/',
    'calendarView': 'calendar/month/',
    'userReview': 'roles/users/',
    'viewUsersFlightReview': 'users/flightreview/',
    'resourceView': 'resources/',
    'locationsManage': 'locations/',
    'rolesManage': 'roles/',
    'logging': 'logging/',
    'mailing': 'mailing/',
    'notificationsManage' : 'notifications/',
    'reportsView' : 'reports/',
    'publicContactsInformation' : 'users/schedule/',
    'productsView' : 'products/'
  
  };

  const getMenuItemUrl = (item) => {
    const path = MENU_URL_MAP[item.unique] || `${item.unique}/`;
    return `${_org}/${path}`;
  };

  return (
    <div className="mb-4">
      <Breadcrumbs
        itemClasses={{
          separator: "px-2",
        }}
        separator="/">
        {/* Home */}
        <BreadcrumbItem>
          <a href="/">Home</a>
        </BreadcrumbItem>

        {/* Dashboard */}
        <BreadcrumbItem>
          <a href="/users/dashboard">Dashboard</a>
        </BreadcrumbItem>

        {/* Organization dropdown - only show if we're in organization context */}
        {orgUnique && (
          <BreadcrumbItem>
            <Dropdown placement="bottom-start">
              <DropdownTrigger>
                <Button
                  variant="light"
                  size="sm"
                  endContent={<ChevronDown className="w-3 h-3" />}
                  className="px-0 min-w-0 h-auto data-[hover=true]:bg-transparent"
                >
                  {orgName}
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label="Organization menu"
                selectionMode="none"
                variant="flat"
                disallowEmptySelection={false}
                itemClasses={{
                  base: [
                    "transition-colors",
                    "data-[hover=true]:bg-default-100",
                    "data-[hover=true]:!bg-opacity-100",
                    "hover:bg-default-100",
                    "hover:!bg-opacity-100",
                    "outline-none",
                    "focus:outline-none",
                    "focus-visible:outline-none",
                  ].join(" "),
                }}
              >
                {isLoading ? (
                  <DropdownItem key="loading" isReadOnly>
                    Loading...
                  </DropdownItem>
                ) : userMenu.length > 0 ? (
                  userMenu
                    .filter(item => {
                      // Hide Training if not enabled in organization settings
                      if (item.name === 'Training' || item.unique === 'training' || item.unique === 'trainingView') {
                        return organizationSettings?.enableTrainingModule;
                      }
                      return true;
                    })
                    .flatMap((item) => {
                    // Training's real menu unique is `trainingView`; it points at the
                    // EXTERNAL training module (env host), NOT `${_org}/trainingView/`.
                    const isTraining =
                      item.unique === 'trainingView' || item.unique === 'training' || item.name === 'Training';
                    // No `href` for Training: it needs a mint-and-launch, not a direct
                    // navigation, and a real anchor here would let middle-click/ctrl-click
                    // reach the unauthenticated URL straight past that. `onPress` still
                    // covers mouse, touch and keyboard (Enter/Space) activation.
                    const menuItem = (
                      <DropdownItem
                        key={item.unique || item.id}
                        href={isTraining ? undefined : getMenuItemUrl(item)}
                        onPress={isTraining ? launchTraining : undefined}
                        className="outline-none focus:outline-none focus-visible:outline-none"
                        classNames={{
                          base: "outline-none focus:outline-none focus-visible:outline-none"
                        }}
                      >
                        {item.name}
                      </DropdownItem>
                    );
                    // Add Aircraft Status after Resources (resourceView)
                    if (item.unique === 'resourceView') {
                      return [
                        menuItem,
                        <DropdownItem
                          key="aircraft-status"
                          href={`${_org}/hangars/`}
                          className="outline-none focus:outline-none focus-visible:outline-none"
                          classNames={{
                            base: "outline-none focus:outline-none focus-visible:outline-none"
                          }}
                        >
                          Aircraft Status
                        </DropdownItem>
                      ];
                    }
                    return [menuItem];
                  })
                ) : (
                  <DropdownItem key="empty" isReadOnly>
                    No menu items
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          </BreadcrumbItem>
        )}

        {/* Custom items (if any) */}
        {customItems.map((item, index) => (
          <BreadcrumbItem key={`custom-${index}`}>
            {item.href ? <a href={item.href}>{item.label}</a> : item.label}
          </BreadcrumbItem>
        ))}

        {/* Current page */}
        {currentPage && (
          <BreadcrumbItem>
            {currentPage}
          </BreadcrumbItem>
        )}
      </Breadcrumbs>
    </div>
  );
};

export default HeroUIBreadcrumb;
